/**
 * Signal hooks that drive the four NEA-5713 connection-health banners.
 * Each hook is pure — it reads from the already-cached SWR hooks
 * (`useShop`, `useInventorySources`, `useInventoryPageSummary`,
 * `useFeedQuality`) and returns either a `{ kind, severity, ... }` object
 * or `null` when the banner shouldn't fire.
 *
 * The `AppLayout` consumes these and renders the matching `HealthBanner`.
 * Splitting the *signal* from the *presentation* keeps the four data
 * conditions testable in isolation and lets the visual components be
 * redesigned (when Max delivers) without touching the conditions.
 *
 * Priority / suppression rules (per the ticket + the product call on
 * ordering):
 *   1. POS-disconnected     suppresses stale-ingest (same underlying cause —
 *                           no point telling the user "your stock is 14
 *                           days stale" when the actual problem is "your
 *                           POS isn't connected")
 *   2. GBP-revoked          independent — Google channel-specific
 *   3. All-stock-rejected   independent — feed-quality signal
 *   4. Stale-ingest         soft / warning / error escalation by age
 */

import { useShop } from '@/hooks/useShop';
import { useFeedQuality } from '@/hooks/useInventory';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const daysSince = (iso) => {
	if (!iso) return null;
	const t = Date.parse(iso);
	if (!Number.isFinite(t)) return null;
	return Math.floor((Date.now() - t) / MS_PER_DAY);
};

/**
 * GBP token revoked / expired. Production currently only writes 'revoked'
 * (gmb-connect/collect-code.js), but we also match 'expired' so the
 * banner is ready the day a writer for that state lands.
 *
 * Suppressed when the shop has a Merchant Centre linkage
 * (`googleMerchantId`). Local Listings + Local Inventory Ads ride on GMC,
 * not on the stored GBP OAuth token — nothing in the codebase reads
 * `gmbConnection.refreshToken` outside gmb-connect's own OAuth flow — and
 * `'revoked'` is only ever written after an interactive re-connect attempt
 * that landed on the `no_business` or `mismatch` paths in
 * `gmb-connect/collect-code.js`. For a shop that already has GMC set up,
 * that leaves a stale `'revoked'` flag while ads and listings keep serving,
 * and the banner (which also promises "Local Ads will stop") is a false
 * alarm (NEA-6195).
 */
export function useGbpHealthBanner() {
	const { shop } = useShop();
	const status = shop?.gmbConnection?.status;
	if (status !== 'revoked' && status !== 'expired') return null;
	if (shop?.googleMerchantId) return null;
	return { kind: 'gbp-revoked', severity: 'urgent', status };
}

/**
 * POS source row disappeared (deleted or `enabled: false`) while the shop
 * has previously ingested. `hasEverIngested` is supplemented onto the shop
 * by `get-my-shops.js` from enabled+disabled inventory-sources rows so the
 * "had a POS once" signal survives the disable.
 *
 * Reads `stockInventorySource` directly off the `useShop()` payload (which
 * `get-my-shops.js` populates from the currently enabled+kind=stock row).
 * The earlier draft used a separate `useInventorySources(shopId)` SWR fetch
 * — but the unify-dashboard service has no `/api/inventory-sources/{shopId}`
 * endpoint configured, so that fetch 404'd, SWR coerced the response to
 * `[]`, and the banner fired on every `hasEverIngested` shop (Bristol etc.).
 * Sourcing this from the same payload that already loaded for the page also
 * removes a late-arriving banner — fixes the layout-shift "jump" when the
 * banner stack pops in after first paint.
 */
export function usePosHealthBanner() {
	const { shop, isLoading } = useShop();
	if (!shop || isLoading) return null;
	if (shop.stockInventorySource) return null;
	if (!shop.hasEverIngested) return null;
	return { kind: 'pos-disconnected', severity: 'urgent' };
}

/**
 * Stock-ingest is older than 7 days. Escalates at 7 / 14 / 30 day
 * thresholds. Suppressed when the POS banner is firing because the POS
 * disconnect is the root cause and showing both makes the same problem
 * look like two different problems.
 *
 * Reads `latestIngest.timestamp` from `shop.stockInventorySource` (already
 * supplied by `get-my-shops.js`). Previously routed through
 * `useInventorySources(shopId)`, but unify-dashboard has no
 * `/api/inventory-sources/{shopId}` endpoint — that fetch 404'd, sources
 * was `[]`, and the stale banner never had real data to read.
 */
export function useStaleIngestBanner() {
	const { shop, isLoading } = useShop();
	const pos = usePosHealthBanner();
	if (pos) return null;
	if (!shop || isLoading) return null;

	const stockSource = shop.stockInventorySource;
	// `.at` is a defensive fallback for any legacy rows where the field was
	// stored at a different key — production writers (`stock-gli-service`,
	// `upload-alerts`) use `.timestamp`.
	const lastSyncAt = stockSource?.latestIngest?.timestamp ?? stockSource?.latestIngest?.at ?? null;
	const days = daysSince(lastSyncAt);
	if (days == null || days < 7) return null;

	let severity = 'soft';
	if (days >= 30) severity = 'error';
	else if (days >= 14) severity = 'warning';
	return { kind: 'stale-ingest', severity, days };
}

/**
 * 100% of the shop's in-stock items have errors blocking visibility on
 * every channel. Reads the feed-quality endpoint (which the retailers
 * dashboard's Feed Quality card already uses).
 */
export function useAllStockRejectedBanner() {
	const { shop } = useShop();
	const { stats } = useFeedQuality(shop?.id);
	if (!stats) return null;
	const { totalItems, erroneousItems } = stats;
	if (!totalItems || totalItems <= 0) return null;
	if (erroneousItems !== totalItems) return null;
	return { kind: 'all-stock-rejected', severity: 'urgent', erroneousItems };
}
